import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';
import {
  CoachBudgetExceededError,
  CoachNotConfiguredError,
  CoachUpstreamError,
} from './coach';

/**
 * Cliente HTTP + hooks para el módulo de Nutrición IA (M2.5).
 *
 * Mismo patrón que `coach.ts` y `strava.ts`: NO usamos el helper `request` de
 * `./api` porque queremos mapear errores específicos del backend (503 sin
 * Anthropic, 503 budget exceeded, 422 plan inválido) a tipos concretos
 * accionables desde la UI.
 *
 * Reutilizamos las clases de error del coach (`CoachNotConfiguredError`,
 * `CoachBudgetExceededError`, `CoachUpstreamError`) porque la causa es la
 * misma (la nutrición usa el mismo SDK Anthropic + budget por profile que el
 * coach), y eso permite que la UI las maneje con un único mapper.
 *
 * Los errores se re-exportan para que los componentes de `features/nutrition`
 * puedan importarlos desde un único módulo sin acoplarse a `coach.ts`.
 */

export {
  CoachBudgetExceededError as NutritionBudgetExceededError,
  CoachNotConfiguredError as NutritionNotConfiguredError,
  CoachUpstreamError as NutritionUpstreamError,
} from './coach';

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

export const MEAL_TYPES = [
  'breakfast',
  'lunch',
  'dinner',
  'snack',
  'pre_workout',
  'post_workout',
] as const;
export type MealType = (typeof MEAL_TYPES)[number];

/**
 * Orden de visualización de comidas en un día. Las comidas relacionadas con
 * el entrenamiento se intercalan según su rol (pre → snack/lunch → post →
 * dinner) para reflejar el flujo real del día.
 */
export const MEAL_ORDER: readonly MealType[] = [
  'breakfast',
  'pre_workout',
  'lunch',
  'snack',
  'post_workout',
  'dinner',
] as const;

export function mealTypeLabel(t: MealType): string {
  switch (t) {
    case 'breakfast':
      return 'Desayuno';
    case 'lunch':
      return 'Comida';
    case 'dinner':
      return 'Cena';
    case 'snack':
      return 'Snack';
    case 'pre_workout':
      return 'Pre-entreno';
    case 'post_workout':
      return 'Post-entreno';
  }
}

export const MEAL_LOG_SOURCES = ['manual', 'planned'] as const;
export type MealLogSource = (typeof MEAL_LOG_SOURCES)[number];

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const MealTypeEnum = z.enum(MEAL_TYPES);

export const MealPlanItemSchema = z.object({
  id: z.number().int(),
  dateISO: z.string(),
  mealType: MealTypeEnum,
  title: z.string(),
  description: z.string().nullable().optional(),
  kcal: z.number().int().nonnegative().nullable().optional(),
  protein_g: z.number().int().nonnegative().nullable().optional(),
  carbs_g: z.number().int().nonnegative().nullable().optional(),
  fat_g: z.number().int().nonnegative().nullable().optional(),
  recipeId: z.number().int().nullable().optional(),
  /**
   * M2.34.A — Receta embebida (LEFT JOIN del backend). El backend la incluye
   * cuando recipeId != null. Usamos `z.lazy` porque `RecipeSchema` se declara
   * DESPUÉS de `MealPlanItemSchema` en este fichero (orden histórico). Si el
   * parse de una recipe concreta fallara, devolvemos null para no romper la
   * deserialización del plan entero (defensa por receta).
   */
  recipe: z
    .unknown()
    .transform((v) => {
      if (v === null || v === undefined) return null;
      const parsed = RecipeSchema.safeParse(v);
      return parsed.success ? parsed.data : null;
    })
    .nullable()
    .optional(),
  notes: z.string().nullable().optional(),
  // M2.36 — pila estimada para el día del meal (1/2/3 o null para neutras).
  // Backend la incluye en respuestas nuevas; los planes pre-M2.36 omiten el
  // campo y el FE lo trata como null.
  pilaLevel: z.number().int().min(1).max(3).nullable().optional(),
});
export type MealPlanItem = z.infer<typeof MealPlanItemSchema>;

export const MealPlanSchema = z.object({
  id: z.number().int(),
  weekStartISO: z.string(),
  generatedAt: z.string(),
  rationale: z.string().nullable().optional(),
  totalKcalEstimated: z.number().int().nonnegative().nullable().optional(),
  items: z.array(MealPlanItemSchema),
});
export type MealPlan = z.infer<typeof MealPlanSchema>;

/**
 * Ingredient schema TOLERANTE a ambos formatos:
 *   - Legacy M2.5: {name, quantity (number|string), unit (string|null)}.
 *   - M2.34.A:    {name, quantityG (number), category (string)}.
 *
 * El backend persiste el formato original con el que se creó la receta
 * (en `recipes.ingredients_json`). Las recetas auto-generadas por el meal
 * plan vienen en formato M2.34.A; las recetas standalone (POST /recipe)
 * siguen en formato legacy. Helpers más abajo (`getIngredientQuantityG`,
 * `getIngredientCategory`, `getIngredientUnit`) normalizan el acceso.
 */
export const RecipeIngredientSchema = z.object({
  name: z.string(),
  // Legacy
  quantity: z.union([z.number(), z.string()]).optional(),
  unit: z.string().nullable().optional(),
  // M2.34.A
  quantityG: z.number().nonnegative().optional(),
  category: z.string().optional(),
});
export type RecipeIngredient = z.infer<typeof RecipeIngredientSchema>;

export const RecipeSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  servings: z.number().int().positive(),
  prep_min: z.number().int().nonnegative().nullable().optional(),
  cook_min: z.number().int().nonnegative().nullable().optional(),
  ingredients: z.array(RecipeIngredientSchema),
  instructions: z.string(),
  kcal_per_serving: z.number().int().nonnegative().nullable().optional(),
  protein_g_per_serving: z.number().int().nonnegative().nullable().optional(),
  carbs_g_per_serving: z.number().int().nonnegative().nullable().optional(),
  fat_g_per_serving: z.number().int().nonnegative().nullable().optional(),
  tags: z.string().nullable().optional(),
  // M2.34.A — array de categorías del backend (parseado desde `recipes.category`
  // JSON). `null` para recetas pre-M2.34. Cuando se omite, la UI lo trata como
  // "sin categorías".
  category: z.array(z.string()).nullable().optional(),
  createdAt: z.string(),
});
export type Recipe = z.infer<typeof RecipeSchema>;

export const MealLoggedSchema = z.object({
  id: z.number().int(),
  datetime: z.string(),
  timezone: z.string(),
  mealType: MealTypeEnum,
  description: z.string(),
  kcal: z.number().int().nonnegative().nullable().optional(),
  protein_g: z.number().int().nonnegative().nullable().optional(),
  carbs_g: z.number().int().nonnegative().nullable().optional(),
  fat_g: z.number().int().nonnegative().nullable().optional(),
  mealPlanItemId: z.number().int().nullable().optional(),
  source: z.enum(MEAL_LOG_SOURCES),
  notes: z.string().nullable().optional(),
});
export type MealLogged = z.infer<typeof MealLoggedSchema>;

// --- Requests ---------------------------------------------------------------

export const MealPlanPreferencesSchema = z.object({
  vegetarian: z.boolean().optional(),
  vegan: z.boolean().optional(),
  lactoseFree: z.boolean().optional(),
  glutenFree: z.boolean().optional(),
  allergies: z.array(z.string()).optional(),
});
export type MealPlanPreferences = z.infer<typeof MealPlanPreferencesSchema>;

export const GenerateMealPlanRequestSchema = z.object({
  weekStartISO: z.string().min(1),
  preferences: MealPlanPreferencesSchema.optional(),
  targetKcal: z.number().int().positive().optional(),
  constraints: z.string().max(1000).optional(),
  preview: z.boolean().optional(),
});
export type GenerateMealPlanRequest = z.infer<typeof GenerateMealPlanRequestSchema>;

export const GenerateMealPlanResponseSchema = z.object({
  plan: MealPlanSchema,
  tokensUsed: z.number().int().nonnegative().optional(),
  latencyMs: z.number().nonnegative().optional(),
  persisted: z.boolean().optional(),
});
export type GenerateMealPlanResponse = z.infer<typeof GenerateMealPlanResponseSchema>;

export const GenerateRecipeRequestSchema = z.object({
  title: z.string().min(1, 'El título es obligatorio').max(200),
  servings: z.number().int().positive().max(50).optional(),
  dietRestrictions: z.string().max(500).optional(),
  additionalNotes: z.string().max(1000).optional(),
});
export type GenerateRecipeRequest = z.infer<typeof GenerateRecipeRequestSchema>;

export const LogMealRequestSchema = z.object({
  datetime: z.string().min(1),
  mealType: MealTypeEnum,
  description: z.string().min(1, 'La descripción es obligatoria').max(500),
  kcal: z.number().int().nonnegative().optional(),
  protein_g: z.number().int().nonnegative().optional(),
  carbs_g: z.number().int().nonnegative().optional(),
  fat_g: z.number().int().nonnegative().optional(),
  mealPlanItemId: z.number().int().optional(),
  notes: z.string().max(1000).optional(),
});
export type LogMealRequest = z.infer<typeof LogMealRequestSchema>;

export const UpdateMealLogRequestSchema = LogMealRequestSchema.partial();
export type UpdateMealLogRequest = z.infer<typeof UpdateMealLogRequestSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers (mismo patrón que strava.ts/coach.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

/**
 * Lanza un error específico para los códigos de error del módulo nutrición
 * que vienen del SDK Anthropic. Reutilizamos las clases del coach.
 */
function throwForAnthropicError(status: number, json: unknown): never {
  const errStr = pickString(json, 'error') ?? '';
  const hint = pickString(json, 'hint') ?? '';
  const detail = pickString(json, 'detail') ?? pickString(json, 'message') ?? '';

  if (status === 503) {
    if (errStr === 'anthropic_budget_exceeded') {
      throw new CoachBudgetExceededError(hint);
    }
    throw new CoachNotConfiguredError(hint);
  }
  if (status === 502 || errStr === 'anthropic_error') {
    throw new CoachUpstreamError();
  }
  if (status === 422) {
    throw new ApiError(detail || 'Respuesta del modelo inválida', 422);
  }
  if (status === 400) {
    throw new ApiError(detail || 'Petición inválida', 400);
  }
  throw new ApiError(`Respuesta no OK del backend: ${status}`, status);
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

async function sendJson<T>(
  path: string,
  schema: z.ZodType<T>,
  body: unknown,
  method: 'POST' | 'PATCH' | 'PUT' = 'POST',
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.ok) {
    const json = await readJsonSafe(response);
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  throwForAnthropicError(response.status, json);
}

async function deleteEmpty(path: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.ok || response.status === 204) return;
  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function generateMealPlan(
  body: GenerateMealPlanRequest,
): Promise<GenerateMealPlanResponse> {
  // Cast: el campo `recipe: z.unknown().transform()` de MealPlanItemSchema
  // produce un tipo opaco que TS infiere como "two different types with same
  // name". El cast es seguro porque el schema es exactamente el mismo.
  return sendJson(
    '/api/nutrition/plan',
    GenerateMealPlanResponseSchema as unknown as z.ZodType<GenerateMealPlanResponse>,
    body,
    'POST',
  );
}

export function getMealPlanWeek(weekStartISO: string): Promise<MealPlan | null> {
  // El backend devuelve 404/204 si no hay plan para esa semana. Lo modelamos
  // como `null` para que la UI pueda mostrar el CTA "Generar plan semanal" sin
  // necesidad de manejar excepciones para un caso esperable.
  const qs = `?weekStartISO=${encodeURIComponent(weekStartISO)}`;
  return getMealPlanWeekInner(`/api/nutrition/week${qs}`);
}

// El backend devuelve `{ plan: MealPlan | null }` (envuelve igual que
// season). Si no hay plan, plan=null (no 404). Mantenemos tolerancia a 404/204
// por defensa con backends antiguos.
const MealPlanWrappedSchema = z.object({ plan: MealPlanSchema.nullable() });

async function getMealPlanWeekInner(path: string): Promise<MealPlan | null> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 404 || response.status === 204) return null;
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  // Permitimos respuestas vacías o `null` como "no hay plan".
  if (json === null || json === undefined) return null;
  // Forma actual: `{ plan: MealPlan | null }`. Forma legacy: `MealPlan` directo.
  const wrapped = MealPlanWrappedSchema.safeParse(json);
  if (wrapped.success) return wrapped.data.plan;
  const direct = MealPlanSchema.safeParse(json);
  if (direct.success) return direct.data;
  throw new ApiError(
    `Respuesta ${path} no cumple esquema`,
    response.status,
    wrapped.error,
  );
}

// El backend envuelve las respuestas de recetas en `{ recipe }` / `{ recipes }`
// para mantener forward-compat (poder añadir metadatos al objeto raíz sin
// cambiar contrato). Aquí desenvolvemos para que los callers reciban directamente
// el dominio.
const GenerateRecipeResponseSchema = z.object({
  recipe: RecipeSchema,
  tokensUsed: z.number().int().nonnegative().optional(),
  latencyMs: z.number().nonnegative().optional(),
  modelUsed: z.string().optional(),
});
const RecipeWrappedSchema = z.object({ recipe: RecipeSchema });
const RecipesWrappedSchema = z.object({ recipes: z.array(RecipeSchema) });

export async function generateRecipe(
  body: GenerateRecipeRequest,
): Promise<Recipe> {
  const res = await sendJson(
    '/api/nutrition/recipe',
    GenerateRecipeResponseSchema,
    body,
    'POST',
  );
  return res.recipe;
}

export async function listRecipes(): Promise<Recipe[]> {
  const res = await getJson('/api/nutrition/recipes', RecipesWrappedSchema);
  return res.recipes;
}

export async function getRecipe(id: number): Promise<Recipe> {
  const res = await getJson(`/api/nutrition/recipes/${id}`, RecipeWrappedSchema);
  return res.recipe;
}

export function deleteRecipe(id: number): Promise<void> {
  return deleteEmpty(`/api/nutrition/recipes/${id}`);
}

// Mismas envolturas para logs: backend devuelve `{ meal }` / `{ meals }`.
const MealWrappedSchema = z.object({ meal: MealLoggedSchema });
const MealsWrappedSchema = z.object({ meals: z.array(MealLoggedSchema) });

export async function logMeal(body: LogMealRequest): Promise<MealLogged> {
  const res = await sendJson('/api/nutrition/log', MealWrappedSchema, body, 'POST');
  return res.meal;
}

export async function updateMealLog(
  id: number,
  patch: UpdateMealLogRequest,
): Promise<MealLogged> {
  const res = await sendJson(
    `/api/nutrition/log/${id}`,
    MealWrappedSchema,
    patch,
    'PATCH',
  );
  return res.meal;
}

export function deleteMealLog(id: number): Promise<void> {
  return deleteEmpty(`/api/nutrition/log/${id}`);
}

export async function listMealLogs(
  fromISO: string,
  toISO: string,
): Promise<MealLogged[]> {
  const qs = `?fromISO=${encodeURIComponent(fromISO)}&toISO=${encodeURIComponent(toISO)}`;
  const res = await getJson(`/api/nutrition/log${qs}`, MealsWrappedSchema);
  return res.meals;
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const NUTRITION_WEEK_KEY = ['nutrition', 'week'] as const;
export const NUTRITION_RECIPES_KEY = ['nutrition', 'recipes'] as const;
export const NUTRITION_LOG_KEY = ['nutrition', 'log'] as const;

export function useMealPlanWeek(
  weekStartISO: string,
): UseQueryResult<MealPlan | null, Error> {
  return useQuery<MealPlan | null, Error>({
    queryKey: [...NUTRITION_WEEK_KEY, weekStartISO],
    queryFn: () => getMealPlanWeek(weekStartISO),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useGenerateMealPlan(): UseMutationResult<
  GenerateMealPlanResponse,
  Error,
  GenerateMealPlanRequest
> {
  const qc = useQueryClient();
  return useMutation<GenerateMealPlanResponse, Error, GenerateMealPlanRequest>({
    mutationFn: (body) => generateMealPlan(body),
    onSuccess: async (data) => {
      // Si se persistió (preview=false), invalidamos la query de la semana
      // afectada para que la vista "Plan semanal" se refresque automáticamente.
      if (data.persisted !== false) {
        await qc.invalidateQueries({ queryKey: NUTRITION_WEEK_KEY });
      }
    },
  });
}

export function useRecipes(): UseQueryResult<Recipe[], Error> {
  return useQuery<Recipe[], Error>({
    queryKey: NUTRITION_RECIPES_KEY,
    queryFn: listRecipes,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useRecipe(id: number | null): UseQueryResult<Recipe, Error> {
  return useQuery<Recipe, Error>({
    queryKey: [...NUTRITION_RECIPES_KEY, id],
    queryFn: () => getRecipe(id as number),
    enabled: id !== null && id !== undefined,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useGenerateRecipe(): UseMutationResult<
  Recipe,
  Error,
  GenerateRecipeRequest
> {
  const qc = useQueryClient();
  return useMutation<Recipe, Error, GenerateRecipeRequest>({
    mutationFn: (body) => generateRecipe(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: NUTRITION_RECIPES_KEY });
    },
  });
}

export function useDeleteRecipe(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteRecipe(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: NUTRITION_RECIPES_KEY });
      // Si una receta borrada estaba referenciada por un plan, la semana podría
      // dejar de resolver `recipeId`. Invalidar también el plan es barato.
      await qc.invalidateQueries({ queryKey: NUTRITION_WEEK_KEY });
    },
  });
}

export function useMealLogs(
  fromISO: string,
  toISO: string,
): UseQueryResult<MealLogged[], Error> {
  return useQuery<MealLogged[], Error>({
    queryKey: [...NUTRITION_LOG_KEY, fromISO, toISO],
    queryFn: () => listMealLogs(fromISO, toISO),
    staleTime: 15_000,
    retry: 1,
  });
}

export function useLogMeal(): UseMutationResult<MealLogged, Error, LogMealRequest> {
  const qc = useQueryClient();
  return useMutation<MealLogged, Error, LogMealRequest>({
    mutationFn: (body) => logMeal(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: NUTRITION_LOG_KEY });
    },
  });
}

export function useUpdateMealLog(): UseMutationResult<
  MealLogged,
  Error,
  { id: number; patch: UpdateMealLogRequest }
> {
  const qc = useQueryClient();
  return useMutation<MealLogged, Error, { id: number; patch: UpdateMealLogRequest }>({
    mutationFn: ({ id, patch }) => updateMealLog(id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: NUTRITION_LOG_KEY });
    },
  });
}

export function useDeleteMealLog(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteMealLog(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: NUTRITION_LOG_KEY });
    },
  });
}

// ===========================================================================
// M2.34.D — Captura rápida de comida (Claude Vision / Texto)
// ===========================================================================
//
// Dos endpoints de análisis SIN persistencia (`POST /log/analyze-photo` y
// `/log/analyze-text`). El usuario revisa el resultado y dispara `useLogMeal`
// para escribirlo en el log definitivo.
//
// Mantenemos las nuevas piezas al final del fichero (bloque comentado con
// el milestone) tal y como acordamos en el contrato multi-agente para
// evitar conflictos de merge con los demás agentes M2.34.

/** Tipo de medio soportado por la API de visión Anthropic. */
export const MEAL_ANALYSIS_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;
export type MealAnalysisMediaType = (typeof MEAL_ANALYSIS_MEDIA_TYPES)[number];

export const MealAnalysisItemSchema = z.object({
  name: z.string(),
  estimatedGrams: z.number().nonnegative(),
});

export const MealAnalysisMacrosSchema = z.object({
  protG: z.number().nonnegative(),
  choG: z.number().nonnegative(),
  fatG: z.number().nonnegative(),
});

export const MealAnalysisSchema = z.object({
  title: z.string(),
  items: z.array(MealAnalysisItemSchema),
  totalKcal: z.number().nonnegative(),
  macros: MealAnalysisMacrosSchema,
  confidence: z.number().min(0).max(1),
});
export type MealAnalysis = z.infer<typeof MealAnalysisSchema>;

const MealAnalysisResponseSchema = z.object({
  analysis: MealAnalysisSchema,
  tokensUsed: z.number().int().nonnegative().optional(),
  latencyMs: z.number().nonnegative().optional(),
  modelUsed: z.string().optional(),
});
export type MealAnalysisResponse = z.infer<typeof MealAnalysisResponseSchema>;

/**
 * Error específico para "el backend requiere api_key para vision" — la UI lo
 * muestra como banner accionable apuntando a /ajustes/anthropic.
 */
export class MealAnalysisVisionUnavailableError extends Error {
  constructor(message?: string) {
    super(
      message ??
        'El análisis por foto requiere una API key de Anthropic configurada.',
    );
    this.name = 'MealAnalysisVisionUnavailableError';
  }
}

/**
 * Error específico para "la IA no devolvió un análisis parseable". La UI
 * sugiere reintentar o cambiar a la pestaña de texto.
 */
export class MealAnalysisInvalidError extends Error {
  constructor(message?: string) {
    super(message ?? 'La IA no devolvió un análisis válido.');
    this.name = 'MealAnalysisInvalidError';
  }
}

async function postAnalyze(
  path: string,
  body: unknown,
): Promise<MealAnalysisResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.ok) {
    const json = await readJsonSafe(response);
    const parsed = MealAnalysisResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  // Errores específicos M2.34.D antes de caer al mapper genérico.
  if (response.status === 501 && pickString(json, 'error') === 'vision_only_api_key') {
    throw new MealAnalysisVisionUnavailableError(pickString(json, 'hint'));
  }
  if (response.status === 422 && pickString(json, 'error') === 'analysis_invalid') {
    throw new MealAnalysisInvalidError(pickString(json, 'hint'));
  }
  if (response.status === 400) {
    const err = pickString(json, 'error');
    if (err === 'image_too_large') {
      throw new ApiError(
        pickString(json, 'hint') ??
          'La imagen supera el tamaño máximo permitido (5 MB).',
        400,
      );
    }
  }
  throwForAnthropicError(response.status, json);
}

export function analyzePhoto(
  imageBase64: string,
  mediaType: MealAnalysisMediaType,
): Promise<MealAnalysisResponse> {
  return postAnalyze('/api/nutrition/log/analyze-photo', {
    imageBase64,
    mediaType,
  });
}

export function analyzeText(text: string): Promise<MealAnalysisResponse> {
  return postAnalyze('/api/nutrition/log/analyze-text', { text });
}

export function useAnalyzePhoto(): UseMutationResult<
  MealAnalysisResponse,
  Error,
  { imageBase64: string; mediaType: MealAnalysisMediaType }
> {
  return useMutation<
    MealAnalysisResponse,
    Error,
    { imageBase64: string; mediaType: MealAnalysisMediaType }
  >({
    mutationFn: ({ imageBase64, mediaType }) => analyzePhoto(imageBase64, mediaType),
  });
}

export function useAnalyzeText(): UseMutationResult<
  MealAnalysisResponse,
  Error,
  { text: string }
> {
  return useMutation<MealAnalysisResponse, Error, { text: string }>({
    mutationFn: ({ text }) => analyzeText(text),
  });
}

// ===========================================================================
// M2.34.B — Lista de la compra + preparaciones (frontend)
// ===========================================================================
//
// Schemas y hooks para los endpoints derivados (no LLM):
//   - GET /api/nutrition/shopping-list?weekStartISO
//   - GET /api/nutrition/prep-tasks?weekStartISO
//
// Bloque añadido al final del archivo siguiendo la regla del contrato
// multi-agente (cada milestone añade su propio bloque comentado).

export const SHOPPING_CATEGORIES = [
  'fresh',
  'pantry',
  'dairy',
  'meat',
  'fish',
  'frozen',
  'otros',
] as const;
export type ShoppingCategory = (typeof SHOPPING_CATEGORIES)[number];

export function shoppingCategoryLabel(c: ShoppingCategory): string {
  switch (c) {
    case 'fresh':
      return 'Frescos';
    case 'pantry':
      return 'Despensa';
    case 'dairy':
      return 'Lácteos';
    case 'meat':
      return 'Carnes';
    case 'fish':
      return 'Pescados';
    case 'frozen':
      return 'Congelados';
    case 'otros':
      return 'Otros';
  }
}

export const ShoppingListItemSchema = z.object({
  name: z.string(),
  totalQuantityG: z.number().nonnegative(),
  unit: z.literal('g'),
  category: z.enum(SHOPPING_CATEGORIES),
  recipes: z.array(
    z.object({
      recipeTitle: z.string(),
      qtyG: z.number().nonnegative(),
    }),
  ),
});
export type ShoppingListItem = z.infer<typeof ShoppingListItemSchema>;

export const ShoppingListResponseSchema = z.object({
  items: z.array(ShoppingListItemSchema),
  reason: z.enum(['no_plan', 'no_recipes_linked']).optional(),
});
export type ShoppingListResponse = z.infer<typeof ShoppingListResponseSchema>;

export const PrepTaskSchema = z.object({
  id: z.string(),
  mealPlanItemId: z.number().int(),
  recipeId: z.number().int(),
  title: z.string(),
  suggestedDatetimeISO: z.string(),
  prepMinutes: z.number().int().nonnegative(),
});
export type PrepTask = z.infer<typeof PrepTaskSchema>;

export const PrepTasksResponseSchema = z.object({
  tasks: z.array(PrepTaskSchema),
});
export type PrepTasksResponse = z.infer<typeof PrepTasksResponseSchema>;

export function getShoppingList(
  weekStartISO: string,
): Promise<ShoppingListResponse> {
  const qs = `?weekStartISO=${encodeURIComponent(weekStartISO)}`;
  return getJson(`/api/nutrition/shopping-list${qs}`, ShoppingListResponseSchema);
}

export function getPrepTasks(weekStartISO: string): Promise<PrepTasksResponse> {
  const qs = `?weekStartISO=${encodeURIComponent(weekStartISO)}`;
  return getJson(`/api/nutrition/prep-tasks${qs}`, PrepTasksResponseSchema);
}

export const NUTRITION_SHOPPING_KEY = ['nutrition', 'shopping-list'] as const;
export const NUTRITION_PREP_KEY = ['nutrition', 'prep-tasks'] as const;

export function useShoppingList(
  weekStartISO: string,
): UseQueryResult<ShoppingListResponse, Error> {
  return useQuery<ShoppingListResponse, Error>({
    queryKey: [...NUTRITION_SHOPPING_KEY, weekStartISO],
    queryFn: () => getShoppingList(weekStartISO),
    staleTime: 30_000,
    retry: 1,
  });
}

export function usePrepTasks(
  weekStartISO: string,
): UseQueryResult<PrepTasksResponse, Error> {
  return useQuery<PrepTasksResponse, Error>({
    queryKey: [...NUTRITION_PREP_KEY, weekStartISO],
    queryFn: () => getPrepTasks(weekStartISO),
    staleTime: 30_000,
    retry: 1,
  });
}

// ===========================================================================
// M2.34.A — Recetas reales en el plan
// ===========================================================================
//
// Helpers de acceso a ingredientes con FALLBACK entre los dos formatos
// soportados por el backend:
//   - Legacy M2.5:  {name, quantity (number|string), unit}.
//   - M2.34.A:     {name, quantityG (number), category}.
//
// Y categorías canónicas de ingredientes (espejo de
// `backend/src/domain/coach/nutrition-tools.ts::INGREDIENT_CATEGORIES`).
//
// Mantenemos este bloque al final del fichero para no chocar con los demás
// agentes M2.34.x según el contrato multi-agente.

export const INGREDIENT_CATEGORIES = [
  'fresh',
  'pantry',
  'dairy',
  'meat',
  'fish',
  'frozen',
  'bakery',
  'other',
] as const;
export type IngredientCategory = (typeof INGREDIENT_CATEGORIES)[number];

export function ingredientCategoryLabel(c: string): string {
  switch (c) {
    case 'fresh':
      return 'Frescos';
    case 'pantry':
      return 'Despensa';
    case 'dairy':
      return 'Lácteos / Huevos';
    case 'meat':
      return 'Carnes';
    case 'fish':
      return 'Pescados';
    case 'frozen':
      return 'Congelados';
    case 'bakery':
      return 'Panadería';
    case 'other':
      return 'Otros';
    default:
      return c;
  }
}

/**
 * Devuelve la cantidad en gramos del ingrediente, normalizando entre formatos.
 * Si la receta es legacy con unit='g' o 'gr', usamos `quantity`. Si es
 * unit='ml' aproximamos g≈ml (defensivo, no perfecto). En cualquier otro
 * caso devolvemos `null` (cantidad desconocida en gramos).
 */
export function getIngredientQuantityG(ing: RecipeIngredient): number | null {
  if (typeof ing.quantityG === 'number') return ing.quantityG;
  if (ing.quantity === undefined || ing.quantity === null) return null;
  const qty =
    typeof ing.quantity === 'number'
      ? ing.quantity
      : Number(String(ing.quantity).replace(',', '.'));
  if (!Number.isFinite(qty)) return null;
  const unit = (ing.unit ?? '').toLowerCase();
  if (unit === 'g' || unit === 'gr' || unit === 'grs' || unit === 'gramos') {
    return qty;
  }
  if (unit === 'ml' || unit === 'cl') {
    return unit === 'cl' ? qty * 10 : qty;
  }
  return null;
}

/** Categoría declarada (M2.34.A) o null si la receta es legacy sin categoría. */
export function getIngredientCategory(ing: RecipeIngredient): string | null {
  return ing.category ?? null;
}

/**
 * Etiqueta humana para mostrar la cantidad — usa el formato original si está
 * disponible (e.g. "2 cdas"), si no usa los gramos M2.34.A.
 */
export function formatIngredientQuantity(ing: RecipeIngredient): string {
  if (typeof ing.quantityG === 'number') {
    // Enteros sin decimales, decimales con un dígito.
    const v = Number.isInteger(ing.quantityG)
      ? ing.quantityG.toString()
      : ing.quantityG.toFixed(1);
    return `${v} g`;
  }
  if (ing.quantity !== undefined && ing.quantity !== null) {
    const u = ing.unit ?? '';
    return u ? `${ing.quantity} ${u}` : String(ing.quantity);
  }
  return '';
}

/**
 * Tiempo total estimado de una receta = prep_min + cook_min. `null` si ambos
 * son nulos.
 */
export function getRecipeTotalMinutes(r: Recipe): number | null {
  const p = r.prep_min ?? null;
  const c = r.cook_min ?? null;
  if (p === null && c === null) return null;
  return (p ?? 0) + (c ?? 0);
}

// ===========================================================================
// M2.36 — Catálogo de recetas (template) + sistema PILAS I/II/III
// ===========================================================================
//
// Endpoint: GET /api/nutrition/recipe-catalog?pila=1|2|3|all&mealType=...&tags=...&q=...
//
// El catálogo expone recetas TEMPLATE (`is_template = true`) seedadas desde
// `backend/db/seed/recipe-catalog-v1.json`. Convive con las recetas de usuario
// (las que están en `recipes` table). Diferencia clave: cada receta lleva un
// `pilaLevel` (I, II o III según carga de entreno del día) y `mealTypes`
// múltiples.
//
// El sistema PILAS reparte el plato visualmente:
//   - PILA I  (descanso o baja carga): + verdura, proteína moderada, CHO bajo.
//   - PILA II (entrenos moderados):    proteína + CHO equilibrados.
//   - PILA III (entrenos largos):      CHO altos, proteína alta, recovery.
//
// Mantenemos este bloque AL FINAL del archivo siguiendo la regla del contrato
// multi-agente (cada milestone añade su propio bloque comentado).

/** Tipos de comida soportados por el catálogo (superset de MEAL_TYPES). */
export const CATALOG_MEAL_TYPES = [
  'breakfast',
  'midmorning',
  'lunch',
  'snack',
  'dinner',
  'pre_workout',
  'post_workout',
] as const;
export type CatalogMealType = (typeof CATALOG_MEAL_TYPES)[number];

export function catalogMealTypeLabel(t: CatalogMealType): string {
  switch (t) {
    case 'breakfast':
      return 'Desayuno';
    case 'midmorning':
      return 'Media mañana';
    case 'lunch':
      return 'Comida';
    case 'snack':
      return 'Snack';
    case 'dinner':
      return 'Cena';
    case 'pre_workout':
      return 'Pre-entreno';
    case 'post_workout':
      return 'Post-entreno';
  }
}

/** Niveles de pila. `null` para recetas neutras (snacks, media mañana). */
export const PILA_LEVELS = [1, 2, 3] as const;
export type PilaLevel = (typeof PILA_LEVELS)[number];

export function pilaLabel(p: PilaLevel | null): string {
  if (p === null) return 'Neutra';
  if (p === 1) return 'PILA I';
  if (p === 2) return 'PILA II';
  return 'PILA III';
}

/**
 * Colores/clases Tailwind para los chips de pila. Mantenemos la paleta
 * consistente con el dossier (I azul claro · II ámbar · III rojo/naranja).
 *
 * Devuelve cadenas estáticas para que el JIT de Tailwind las detecte (no
 * interpolaciones).
 */
export function pilaBadgeClass(p: PilaLevel | null): string {
  switch (p) {
    case 1:
      return 'bg-sky-100 text-sky-800 ring-sky-200';
    case 2:
      return 'bg-amber-100 text-amber-800 ring-amber-200';
    case 3:
      return 'bg-orange-100 text-orange-900 ring-orange-300';
    default:
      return 'bg-slate-100 text-slate-700 ring-slate-200';
  }
}

/** Tags canónicos esperados en el catálogo (alineado con el seed v1). */
export const CATALOG_TAGS = [
  'anti_reflux',
  'anti_inflammatory',
  'vegetarian',
  'vegan',
  'quick_15min',
  'batch_cookable',
  'high_carb',
  'glutenfree',
  'dairyfree',
] as const;
export type CatalogTag = (typeof CATALOG_TAGS)[number];

export function catalogTagLabel(t: string): string {
  switch (t) {
    case 'anti_reflux':
      return 'Anti-reflujo';
    case 'anti_inflammatory':
      return 'Antiinflamatoria';
    case 'vegetarian':
      return 'Vegetariana';
    case 'vegan':
      return 'Vegana';
    case 'quick_15min':
      return 'Rápida (≤15 min)';
    case 'batch_cookable':
      return 'Batch cooking';
    case 'high_carb':
      return 'Alta en CHO';
    case 'glutenfree':
      return 'Sin gluten';
    case 'dairyfree':
      return 'Sin lácteos';
    case 'dairy_optional':
      return 'Lácteo opcional';
    default:
      return t.replace(/_/g, ' ');
  }
}

export const RecipeCatalogMacrosSchema = z.object({
  kcal: z.number().nonnegative(),
  protG: z.number().nonnegative(),
  choG: z.number().nonnegative(),
  fatG: z.number().nonnegative(),
});
export type RecipeCatalogMacros = z.infer<typeof RecipeCatalogMacrosSchema>;

/**
 * Ingrediente del catálogo. Idéntico al M2.34.A (`name + quantityG + category`)
 * — el catálogo es un superset estricto: nunca trae la forma legacy
 * `quantity + unit`.
 */
export const RecipeCatalogIngredientSchema = z.object({
  name: z.string(),
  quantityG: z.number().nonnegative(),
  category: z.string(),
});
export type RecipeCatalogIngredient = z.infer<
  typeof RecipeCatalogIngredientSchema
>;

export const RecipeCatalogItemSchema = z.object({
  id: z.union([z.number().int(), z.string()]),
  title: z.string(),
  isTemplate: z.boolean().default(true),
  // `pilaLevel` puede ser null para recetas neutras (snacks, media mañana).
  pilaLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
  tags: z.array(z.string()).default([]),
  mealTypes: z.array(z.string()).default([]),
  macros: RecipeCatalogMacrosSchema,
  prepMin: z.number().int().nonnegative().default(0),
  cookMin: z.number().int().nonnegative().default(0),
  ingredients: z.array(RecipeCatalogIngredientSchema).default([]),
  steps: z.array(z.string()).default([]),
  servingsDefault: z.number().int().positive().default(1),
});
export type RecipeCatalogItem = z.infer<typeof RecipeCatalogItemSchema>;

export const RecipeCatalogResponseSchema = z.object({
  recipes: z.array(RecipeCatalogItemSchema),
});
export type RecipeCatalogResponse = z.infer<typeof RecipeCatalogResponseSchema>;

/** Filtros para `getRecipeCatalog`. Todos opcionales. */
export interface RecipeCatalogFilters {
  pila?: PilaLevel | 'all';
  mealType?: CatalogMealType | CatalogMealType[];
  tags?: string[];
  q?: string;
}

function buildCatalogQs(filters: RecipeCatalogFilters | undefined): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.pila !== undefined) {
    params.set('pila', filters.pila === 'all' ? 'all' : String(filters.pila));
  }
  if (filters.mealType !== undefined) {
    const arr = Array.isArray(filters.mealType)
      ? filters.mealType
      : [filters.mealType];
    if (arr.length > 0) params.set('mealType', arr.join(','));
  }
  if (filters.tags && filters.tags.length > 0) {
    params.set('tags', filters.tags.join(','));
  }
  if (filters.q && filters.q.trim().length > 0) {
    params.set('q', filters.q.trim());
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getRecipeCatalog(
  filters?: RecipeCatalogFilters,
): Promise<RecipeCatalogItem[]> {
  const path = `/api/nutrition/recipe-catalog${buildCatalogQs(filters)}`;
  // Cast del schema: los `.default(...)` hacen los campos opcionales en el
  // input pero requeridos en el output (siempre con valor). Zod aplica el
  // default ANTES de devolver `data`, así que el cast es seguro.
  const res = await getJson(
    path,
    RecipeCatalogResponseSchema as unknown as z.ZodType<RecipeCatalogResponse>,
  );
  return res.recipes;
}

export const NUTRITION_RECIPE_CATALOG_KEY = [
  'nutrition',
  'recipe-catalog',
] as const;

/**
 * Hook para el catálogo. Devuelve una `UseQueryResult` para mantener semántica
 * cacheable (lo invocan `RecipeCatalogView` y `ChangeRecipeModal`, ambos con
 * filtros distintos — la queryKey incluye los filtros normalizados para que
 * TanStack Query cache cada combinación de forma independiente).
 */
export function useRecipeCatalog(
  filters?: RecipeCatalogFilters,
): UseQueryResult<RecipeCatalogItem[], Error> {
  // Normalizamos los filtros para que la queryKey sea estable:
  // - mealType siempre array ordenado alfabéticamente
  // - tags ordenados alfabéticamente
  // - q trim + lowercase
  const normalized = {
    pila: filters?.pila ?? null,
    mealType: filters?.mealType
      ? (Array.isArray(filters.mealType)
          ? [...filters.mealType]
          : [filters.mealType]
        ).sort()
      : [],
    tags: filters?.tags ? [...filters.tags].sort() : [],
    q: filters?.q?.trim().toLowerCase() ?? '',
  };
  return useQuery<RecipeCatalogItem[], Error>({
    queryKey: [...NUTRITION_RECIPE_CATALOG_KEY, normalized],
    queryFn: () => getRecipeCatalog(filters),
    staleTime: 5 * 60_000, // catálogo cambia rara vez
    retry: 1,
  });
}

/**
 * Estima la pila aproximada de un día a partir del workout principal.
 * Heurística simple (front-only, no autoritativa — el backend puede derivar
 * algo más fino con TSS):
 *   - Sin entreno o ≤30 min ligero        → PILA I.
 *   - 30-75 min moderado o series cortas  → PILA II.
 *   - ≥75 min o tirada larga / sesión Z4+ → PILA III.
 *
 * Se usa SOLO en `PilasInfoView` para mostrar la pila "estimada" de los
 * próximos días. El plan generado por el backend lleva su propia pila por día.
 */
export interface PilaDayEstimate {
  pila: PilaLevel | null;
  reason: string;
}

// ---------------------------------------------------------------------------
// Mutación: cambiar la receta de un item del plan
// ---------------------------------------------------------------------------
//
// El backend M2.36 expone `PATCH /api/nutrition/plan-item/:id` para actualizar
// `recipeId` y `servings` (y recomputar macros). El cliente lo invoca desde
// `ChangeRecipeModal` a través del `useChangeMealPlanItem` hook.

export const ChangeMealPlanItemRequestSchema = z.object({
  recipeId: z.union([z.number().int(), z.string()]),
  servings: z.number().int().positive().max(20),
});
export type ChangeMealPlanItemRequest = z.infer<
  typeof ChangeMealPlanItemRequestSchema
>;

export const ChangeMealPlanItemResponseSchema = z.object({
  item: MealPlanItemSchema,
});
export type ChangeMealPlanItemResponse = z.infer<
  typeof ChangeMealPlanItemResponseSchema
>;

export async function changeMealPlanItemRecipe(
  itemId: number,
  body: ChangeMealPlanItemRequest,
): Promise<MealPlanItem> {
  // Cast por el mismo motivo que `generateMealPlan`: el `z.unknown().transform()`
  // de `recipe` en `MealPlanItemSchema` hace que TS no reconozca la identidad
  // del tipo cuando lo referencia desde dos lugares distintos.
  const res = await sendJson(
    `/api/nutrition/plan-item/${itemId}`,
    ChangeMealPlanItemResponseSchema as unknown as z.ZodType<ChangeMealPlanItemResponse>,
    body,
    'PATCH',
  );
  return res.item;
}

export function useChangeMealPlanItem(): UseMutationResult<
  MealPlanItem,
  Error,
  { itemId: number; body: ChangeMealPlanItemRequest }
> {
  const qc = useQueryClient();
  return useMutation<
    MealPlanItem,
    Error,
    { itemId: number; body: ChangeMealPlanItemRequest }
  >({
    mutationFn: ({ itemId, body }) => changeMealPlanItemRecipe(itemId, body),
    onSuccess: async () => {
      // El plan semanal cambió: invalidamos para refrescar la grid.
      await qc.invalidateQueries({ queryKey: NUTRITION_WEEK_KEY });
    },
  });
}

export function estimatePilaForWorkout(input: {
  durationMin: number | null | undefined;
  discipline: string | null | undefined;
  intensityZone: string | null | undefined;
}): PilaDayEstimate {
  const dur = input.durationMin ?? 0;
  const zone = (input.intensityZone ?? '').toLowerCase();
  const disc = input.discipline ?? 'descanso';
  if (dur <= 0) {
    return { pila: 1, reason: 'descanso → PILA I' };
  }
  const isHigh = /z4|z5|umbral|vo2|series|interval/.test(zone);
  if (dur >= 75 || (isHigh && dur >= 45)) {
    return {
      pila: 3,
      reason: `${disc} ${dur} min${isHigh ? ' intenso' : ' largo'} → PILA III`,
    };
  }
  if (dur >= 30) {
    return {
      pila: 2,
      reason: `${disc} ${dur} min${isHigh ? ' (series)' : ''} → PILA II`,
    };
  }
  return { pila: 1, reason: `${disc} ${dur} min suave → PILA I` };
}
